import { HANA_CLIENT_INFO, configFromClientInfo, createWorkCalendar } from '@hana/work-calendar';
import { COSTING_JOBS } from './costingJobs';
import type { CostingCalc, CostingGroup, CostingJobDef, CostingRow, CostingWarn, MatchKind } from './costingTypes';
import { f1 } from './view';

// Costing theo Job (Mint-Cost › Chi tiết): chép COSTING ENGINE v1 của board. Chỉ đọc — mọi nút ghi báo toast.

export const VCB = 25420; // VCB bán 22/Oct/2026 (minh hoạ)
export const DUP = 0.05; // lệch < 0,05 tr ₫ = trùng / khớp
export const CA = 200; // chi ≥ 200 tr thêm bước ACC-CA
export const TODAY = '22/Oct/2026';
export const LOCK_AT = '05/Nov/2026';
export const ACC_CP = '[ACC-CP] HANHLTM';
export const ACC_AR = '[ACC-AR] NGOCANH';
export const ACC_CA = '[ACC-CA] ghế trống → BOD-DIR PHILIP';
const DUE = { frt: 5, plus: 30 };
const SOON = 2;
const PLUS: Readonly<Record<string, string>> = { Trucking: 'TRF', 'Hải quan': 'CCF', 'Khai HQ': 'CCF', 'Hun trùng': 'FUM', 'Kiểm dịch': 'PTF', CO: 'COO', Kho: 'SCY', 'Bảo hiểm': 'ISR' };
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Ngày làm việc chỉ qua `work-calendar` (nguồn tblClientInfo). Board ghi `dd/Mon/yyyy`, calendar dùng ISO. */
const cal = createWorkCalendar(configFromClientInfo(HANA_CLIENT_INFO));
const toIso = (s: string): string => {
  const m = /^(\d{2})\/(\w{3})\/(\d{4})/.exec(s);
  return m ? `${m[3]}-${String(MO.indexOf(m[2]!) + 1).padStart(2, '0')}-${m[1]}` : s;
};
const fromIso = (s: string): string => `${s.slice(8, 10)}/${MO[+s.slice(5, 7) - 1]}/${s.slice(0, 4)}`;

export const usd = (x: number): string => Math.round(x).toLocaleString('vi-VN');

function deadlineOf(j: CostingJobDef, open: boolean): CostingCalc['deadline'] {
  if (j.fd === null || !j.fin) return null;
  const pl = [...new Set(j.p.map((p) => PLUS[p[0]]).filter((x): x is string => !!x))];
  const n = pl.length ? DUE.plus : DUE.frt;
  const due = fromIso(cal.addWorkDays(toIso(j.fin), n));
  const left = cal.workDaysBetween(toIso(TODAY), toIso(due));
  const kind = pl.length ? 'có plus services' : 'thuần cước';
  const lv = !open ? '' : left < 0 ? 'r' : left <= SOON ? 'o' : '';
  return { due, left, kind, lv, tip: `Hạn duyệt costing ${due} · ${n} ngày làm việc sau mốc Finish job ${j.fin} (${kind}${pl.length ? ': ' + pl.join(', ') : ''}) · ngày làm việc theo WorkCalendar (tblClientInfo)` };
}

/** Nhãn nút chính theo trạng thái (board `prim`). */
export function primaryLabel(j: CostingJobDef, cost: number): string {
  if (j.s === 'draft') return 'Gửi duyệt';
  if (j.s === 'pend') return !j.g[0] || !j.g[1] ? 'Duyệt costing' : cost >= CA ? 'Duyệt · ACC-CP' : 'Duyệt costing';
  return j.s === 'appr' ? 'Mở khoá' : 'Xin mở kỳ';
}

const matchOf = (have: number | null, buy: number): MatchKind => (have === null ? 'none' : Math.abs(have - buy) < DUP ? 'ok' : 'diff');

export function calcCosting(j: CostingJobDef): CostingCalc {
  const P: CostingRow[] = j.p.map((p, k) => ({
    key: `${COSTING_JOBS.indexOf(j)}-${k}`, svc: p[0], code: p[1], name: p[2], note: p[6] || '', sell: p[3], buy: p[4], q: p[5],
    sTr: p[3] ? p[3][1] : 0, bTr: p[4] ? p[4][1] : 0, dup: false, dn: null, inv: null, debit: null, lock: false, est: false,
  }));
  let rev = 0, cost = 0, qRev = 0, qCost = 0, pRev = 0, pCost = 0;
  P.forEach((p, x) => {
    rev += p.sTr; cost += p.bTr;
    if (p.q) { qRev += p.q[0]; qCost += p.q[1]; }
    pRev += p.sell ? p.sTr : p.q ? p.q[0] : 0;
    pCost += p.buy ? p.bTr : p.q ? p.q[1] : 0;
    p.dup = !!p.buy && P.slice(0, x).some((r) => r.buy && r.code === p.code && r.buy[0] === p.buy![0] && Math.abs(r.buy[1] - p.buy![1]) < DUP);
    p.dn = p.buy ? matchOf(p.buy[4], p.buy[1]) : null;
    p.inv = p.buy ? matchOf(p.buy[5], p.buy[1]) : null;
    p.debit = p.sell ? p.sell[4] : null;
    p.lock = p.debit === 'sent';
    p.est = !!(p.buy && p.buy[6]);
  });
  const gp = rev - cost;
  const fin = j.fd !== null;
  const proj = pRev - pCost;
  const open = j.s === 'draft' || j.s === 'pend';
  const dl = deadlineOf(j, open);
  const priLabel = primaryLabel(j, cost);
  const W: Omit<CostingWarn, 'id'>[] = [];
  if (fin && gp < -0.0001) W.push({ lv: 'r', k: 'Lỗ', v: `${f1(gp)} tr`, tip: 'Lãi gộp thực tế âm · rà các dòng cờ cam bên dưới trước khi duyệt' });
  else if (!fin && proj < -0.0001) W.push({ lv: 'o', k: 'Dự kiến lỗ', v: `${f1(proj)} tr`, tip: 'Job chưa Finish · lãi dự kiến = bán (hoặc giá bán báo giá) − mua (hoặc giá mua báo giá)' });
  P.forEach((p) => {
    const b = p.buy;
    if (p.dup && b) W.push({ lv: 'r', row: p.key, k: `Dòng trùng · ${p.code}`, v: `${f1(p.bTr)} tr`, tip: `Cùng mã phí + cùng đối tác ${b[0]}, lệch < 0,05 tr ₫`, act: 'Gộp' });
    if (p.sell && b && p.sTr < p.bTr - DUP) W.push({ lv: 'o', row: p.key, k: `Bán dưới giá mua · ${p.code}`, v: `${f1(p.sTr - p.bTr)} tr`, tip: 'Giá bán dưới giá mua phải xin duyệt', act: 'Xin duyệt giá' });
    if (b && !p.sell && !p.dup) W.push({ lv: 'o', row: p.key, k: `Mua chưa có phí bán · ${p.code}`, v: `${f1(p.bTr)} tr`, tip: `Có dòng mua ${b[0]} nhưng chưa có dòng bán cùng mã${p.q ? ` · báo giá có giá bán ${f1(p.q[0])} tr` : ''}`, act: p.q ? 'Nạp phí' : undefined });
    if (b && p.dn === 'diff' && b[4] !== null) W.push({ lv: 'o', row: p.key, k: `DN đối tác lệch · ${p.code}`, v: `${f1(b[4] - b[1])} tr`, tip: `DN ${b[0]} ${f1(b[4])} tr ≠ dòng mua ${f1(b[1])} tr · ACC-CP duyệt lệch`, act: 'Duyệt lệch' });
    if (b && p.inv === 'diff' && b[5] !== null) W.push({ lv: 'o', row: p.key, k: `HĐ mua vào lệch · ${p.code}`, v: `${f1(b[5] - b[1])} tr`, tip: `HĐ ${f1(b[5])} tr ≠ dòng mua ${f1(b[1])} tr · ACC-CP duyệt lệch hoặc chờ HĐ điều chỉnh`, act: 'Duyệt lệch' });
    else if (b && p.inv === 'none' && fin && (j.fd as number) > 7) W.push({ lv: 'o', row: p.key, k: `Thiếu HĐ mua vào · ${p.code}`, v: `+${j.fd} ngày`, tip: `Finish ${j.fin} · quá 7 ngày chưa có HĐ ${b[0]}`, act: 'Nhắc đối tác' });
    if (p.sell && p.sell[2] !== 'VND' && p.sell[3] !== VCB) {
      const d = (p.sell[1] * VCB) / p.sell[3] - p.sell[1];
      W.push({ lv: 'o', row: p.key, k: `Tỷ giá lệch VCB · ${p.code}`, v: `${d > 0 ? '+' : ''}${f1(d)} tr`, tip: `Dòng dùng ${usd(p.sell[3])} · VCB bán 22/Oct ${usd(VCB)}`, act: 'Áp VCB' });
    }
  });
  if (dl && dl.lv) {
    W.push({
      lv: dl.lv, prim: true, v: '', tip: dl.tip, act: priLabel,
      k: `${dl.lv === 'r' ? 'Trễ' : 'Sắp trễ'} duyệt costing · ${dl.lv === 'r' ? -dl.left + ' ngày' : 'còn ' + dl.left + ' ngày'} (${dl.kind}, hạn ${dl.kind === 'thuần cước' ? DUE.frt : DUE.plus} ngày)`,
    });
  }
  W.sort((a, b) => (a.lv === b.lv ? 0 : a.lv === 'r' ? -1 : 1));
  const idx = COSTING_JOBS.indexOf(j);
  const warns = W.map((w, x) => ({ ...w, id: `${idx}-w${x}` }));
  const svc: CostingGroup[] = [];
  P.forEach((p) => {
    let g = svc.find((x) => x.k === p.svc);
    if (!g) { g = { k: p.svc, rev: 0, exp: 0, act: 0, notes: [], ps: [] }; svc.push(g); }
    g.rev += p.sTr; g.exp += p.q ? p.q[1] : 0; g.act += p.bTr;
    if (p.note) g.notes.push(p.note);
    g.ps.push(p);
  });
  const invOk = P.filter((p) => p.buy).reduce((a, p) => a + (p.inv === 'ok' ? p.bTr : 0), 0);
  const wr = warns.filter((w) => w.lv === 'r').length;
  return { job: j, P, rev, cost, gp, mg: rev ? (gp / rev) * 100 : 0, qRev, qCost, proj, fin, W: warns, wr, wo: warns.length - wr, svc, invPt: cost ? Math.round((invOk / cost) * 100) : 0, priLabel, deadline: dl };
}

export const costingByNo = (no: string): CostingCalc | undefined => {
  const j = COSTING_JOBS.find((x) => x.no === no);
  return j ? calcCosting(j) : undefined;
};
export const COSTING_DETAIL_DEFAULT = 'SE260617270';
